import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Card,
    Drawer,
    Popconfirm,
    Tooltip,
    InputNumber,
    Typography,
    Divider,
    List
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    SafetyOutlined,
    SyncOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import { useTranslation } from '../../../shared/i18n';
import SgRuleFields, { RULE_DEFAULTS, rulePayload, FamilyTag, protocolLabel, remoteLabel, portsLabel } from '../../../shared/components/evm/SgRuleFields';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const SecurityGroupManagement = () => {
    const { t } = useTranslation();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [rulesModalVisible, setRulesModalVisible] = useState(false);
    const [addRuleModalVisible, setAddRuleModalVisible] = useState(false);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [rulesLoading, setRulesLoading] = useState(false);

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const [ruleForm] = Form.useForm();

    useEffect(() => {
        loadGroups();
    }, []);

    const loadGroups = async () => {
        setLoading(true);
        try {
            const response = await evmAdminService.getSecurityGroupList();
            if (response.status === 'success') {
                const groupsData = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.data || response.data?.groups || []);
                setGroups(groupsData);
            } else {
                message.error(response.msg || 'Failed to load security groups');
            }
        } catch (error) {
            message.error('Failed to load security groups');
        } finally {
            setLoading(false);
        }
    };

    const loadGroupDetail = async (groupId) => {
        setRulesLoading(true);
        try {
            const response = await evmAdminService.getSecurityGroup(groupId);
            if (response.status === 'success') {
                setCurrentGroup(response.data);
            } else {
                message.error(response.msg || 'Failed to load security group');
            }
        } catch (error) {
            message.error('Failed to load security group');
        } finally {
            setRulesLoading(false);
        }
    };

    const showCreateDrawer = () => {
        createForm.resetFields();
        setCreateDrawerVisible(true);
    };

    const showEditDrawer = (group) => {
        setCurrentGroup(group);
        editForm.setFieldsValue(group);
        setEditDrawerVisible(true);
    };

    const showRulesModal = async (group) => {
        setCurrentGroup(group);
        await loadGroupDetail(group.id);
        setRulesModalVisible(true);
    };

    const showAddRuleModal = () => {
        ruleForm.resetFields();
        ruleForm.setFieldsValue(RULE_DEFAULTS);
        setAddRuleModalVisible(true);
    };

    const handleCreate = async (values) => {
        try {
            const response = await evmAdminService.createSecurityGroup(values);
            if (response.status === 'success') {
                message.success('Security group created');
                setCreateDrawerVisible(false);
                loadGroups();
            } else {
                message.error(response.msg || 'Failed to create security group');
            }
        } catch (error) {
            message.error('Failed to create security group');
        }
    };

    const handleUpdate = async (values) => {
        try {
            const response = await evmAdminService.updateSecurityGroup(currentGroup.id, values);
            if (response.status === 'success') {
                message.success('Security group updated');
                setEditDrawerVisible(false);
                loadGroups();
            } else {
                message.error(response.msg || 'Failed to update security group');
            }
        } catch (error) {
            message.error('Failed to update security group');
        }
    };

    const handleDelete = async (group) => {
        try {
            const response = await evmAdminService.deleteSecurityGroup(group.id);
            if (response.status === 'success') {
                message.success('Security group deleted');
                loadGroups();
            } else {
                message.error(response.msg || 'Delete failed');
            }
        } catch (error) {
            message.error('Delete failed');
        }
    };

    const handleAddRule = async (values) => {
        const payload = rulePayload(values);
        try {
            const response = await evmAdminService.addSecurityGroupRule(currentGroup.id, payload);
            if (response.status === 'success') {
                message.success('Rule added');
                setAddRuleModalVisible(false);
                loadGroupDetail(currentGroup.id);
            } else {
                message.error(response.msg || 'Failed to add rule');
            }
        } catch (error) {
            message.error('Failed to add rule');
        }
    };

    const handleDeleteRule = async (ruleId) => {
        try {
            const response = await evmAdminService.deleteSecurityGroupRule(currentGroup.id, ruleId);
            if (response.status === 'success') {
                message.success('Rule deleted');
                loadGroupDetail(currentGroup.id);
            } else {
                message.error(response.msg || 'Failed to delete rule');
            }
        } catch (error) {
            message.error('Failed to delete rule');
        }
    };

    const getDirectionTag = (direction) => {
        return direction === 'ingress' ? (
            <Tag color="green" icon={<ArrowLeftOutlined />}>Ingress</Tag>
        ) : (
            <Tag color="blue" icon={<ArrowRightOutlined />}>Egress</Tag>
        );
    };

    const getActionTag = (action) => {
        return action === 'accept' ? (
            <Tag color="green">ACCEPT</Tag>
        ) : (
            <Tag color="red">DROP</Tag>
        );
    };

    const getProtocolTag = (protocol) => {
        const colors = {
            'tcp': 'blue',
            'udp': 'purple',
            'icmp': 'orange',
            'icmpv6': 'orange',
            'all': 'default'
        };
        return <Tag color={colors[protocol] || 'default'}>{protocolLabel(protocol, t)}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 150
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'User ID',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 80
        },
        {
            title: 'Rules',
            dataIndex: 'rule_count',
            key: 'rule_count',
            width: 80,
            render: (count) => count || 0
        },
        {
            title: 'VMs',
            dataIndex: 'vm_count',
            key: 'vm_count',
            width: 80,
            render: (count) => count || 0
        },
        {
            title: 'Default',
            dataIndex: 'is_default',
            key: 'is_default',
            width: 80,
            render: (isDefault) => isDefault ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>
        },
        {
            title: 'Actions',
            key: 'action',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="View Rules">
                        <Button
                            size="small"
                            icon={<SafetyOutlined />}
                            onClick={() => showRulesModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => showEditDrawer(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this security group?"
                        description="All rules will be removed."
                        onConfirm={() => handleDelete(record)}
                        disabled={record.is_default}
                    >
                        <Tooltip title={record.is_default ? "Cannot delete default group" : "Delete"}>
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={record.is_default}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const ruleColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: 'Direction',
            dataIndex: 'direction',
            key: 'direction',
            width: 100,
            render: (dir) => getDirectionTag(dir)
        },
        {
            title: 'Protocol',
            dataIndex: 'protocol',
            key: 'protocol',
            width: 80,
            render: (proto) => getProtocolTag(proto)
        },
        {
            title: t('Address families'),
            key: 'family',
            width: 80,
            render: (_, record) => <FamilyTag rule={record} />
        },
        {
            title: 'Port Range',
            key: 'ports',
            width: 120,
            render: (_, record) => portsLabel(record, t)
        },
        {
            title: 'Source/Dest',
            key: 'remote_ip_prefix',
            width: 140,
            render: (_, record) => remoteLabel(record, t)
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 80,
            render: (action) => getActionTag(action)
        },
        {
            title: 'Priority',
            dataIndex: 'priority',
            key: 'priority',
            width: 80
        },
        {
            title: '',
            key: 'actions',
            width: 60,
            render: (_, record) => (
                <Popconfirm
                    title="Delete this rule?"
                    onConfirm={() => handleDeleteRule(record.id)}
                >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    const GroupForm = ({ form, onFinish, isEdit = false }) => (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: 'Please enter name' }]}
            >
                <Input placeholder="e.g., web-server-sg" />
            </Form.Item>

            <Form.Item name="description" label="Description">
                <TextArea rows={3} placeholder="Security group description" />
            </Form.Item>

            {!isEdit && (
                <Form.Item
                    name="user_id"
                    label="User ID"
                    rules={[{ required: true, message: 'Please enter user ID' }]}
                >
                    <InputNumber min={1} style={{ width: '100%' }} placeholder="Owner user ID" />
                </Form.Item>
            )}

            <Form.Item
                name="is_default"
                label="Default Group"
                valuePropName="checked"
                initialValue={false}
            >
                <Select>
                    <Option value={true}>Yes</Option>
                    <Option value={false}>No</Option>
                </Select>
            </Form.Item>
        </Form>
    );

    return (
        <div>
            <Card
                title={<span><SafetyOutlined /> 安全组管理</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showCreateDrawer}>
                            Create Security Group
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadGroups}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={groups}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1000 }}
                    pagination={{ pageSize: 20 }}
                />
            </Card>

            {/* Create Security Group Drawer */}
            <Drawer
                title="创建安全组"
                width={450}
                open={createDrawerVisible}
                onClose={() => setCreateDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setCreateDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => createForm.submit()}>Create</Button>
                    </Space>
                }
            >
                <GroupForm form={createForm} onFinish={handleCreate} />
            </Drawer>

            {/* Edit Security Group Drawer */}
            <Drawer
                title="编辑安全组"
                width={450}
                open={editDrawerVisible}
                onClose={() => setEditDrawerVisible(false)}
                extra={
                    <Space>
                        <Button onClick={() => setEditDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" onClick={() => editForm.submit()}>Save</Button>
                    </Space>
                }
            >
                <GroupForm form={editForm} onFinish={handleUpdate} isEdit />
            </Drawer>

            {/* Rules Modal */}
            <Modal
                title={`Rules: ${currentGroup?.name || ''}`}
                open={rulesModalVisible}
                onCancel={() => setRulesModalVisible(false)}
                footer={[
                    <Button key="add" type="primary" icon={<PlusOutlined />} onClick={showAddRuleModal}>
                        Add Rule
                    </Button>,
                    <Button key="close" onClick={() => setRulesModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={900}
            >
                <Table
                    columns={ruleColumns}
                    dataSource={currentGroup?.rules || []}
                    loading={rulesLoading}
                    rowKey="id"
                    size="small"
                    pagination={false}
                />
            </Modal>

            {/* Add Rule Modal */}
            <Modal
                title="添加安全组规则"
                open={addRuleModalVisible}
                onCancel={() => setAddRuleModalVisible(false)}
                onOk={() => ruleForm.submit()}
            >
                <Form form={ruleForm} layout="vertical" onFinish={handleAddRule}>
                    <SgRuleFields form={ruleForm} />

                    <Form.Item name="priority" label="Priority" initialValue={100}>
                        <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="description" label="Description">
                        <Input placeholder="Rule description" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SecurityGroupManagement;
